import { useMemo } from 'react'
import { endOfDay, isSameDay, startOfDay } from 'date-fns'

import {
    gteFloatFilter,
    lteFloatFilter,
    iLikeStringFilter,
    inStringArrFilter,
    eqBoolFilter,
} from '~utils/query-helpers'
import {
    QueryInput,
    OrderByInput,
    CallDirection,
    CallStatus,
} from '~graphql/generated/graphql'
import { SetStateFn, PageSizeType } from '~config/constants'
import { usePageControls } from '~utils/hooks/use-page-controls'
import { useStateWithDebounce } from '~utils/hooks/use-state-with-debounce'

export enum CallSearchField {
    CallerName = 'CallerName',
    CallerPhone = 'CallerPhone',
    ClientFirstName = 'ClientFirstName',
    ClientLastName = 'ClientLastName',
    ClientPhone = 'ClientPhone',
}

export const getCallSearchPlaceholder = (field: CallSearchField): string =>
    ({
        [CallSearchField.CallerName]: 'caller name',
        [CallSearchField.CallerPhone]: 'caller phone',
        [CallSearchField.ClientFirstName]: 'client first name',
        [CallSearchField.ClientLastName]: 'client last name',
        [CallSearchField.ClientPhone]: 'client phone',
    })[field]

const getCallSearchColumn = (field: CallSearchField): string =>
    ({
        [CallSearchField.CallerName]: 'caller_name',
        [CallSearchField.CallerPhone]: 'caller_phone',
        [CallSearchField.ClientFirstName]: 'client.first_name',
        [CallSearchField.ClientLastName]: 'client.last_name',
        [CallSearchField.ClientPhone]: 'client.phone',
    })[field]

export function useCallQueryInput(): QueryInputType & QueryInputValues {
    const [dateRange, setDateRange, debouncedDates] = useStateWithDebounce<
        Date | Date[] | undefined
    >(undefined)
    const [searchField, setSearchField, debouncedSearchField] =
        useStateWithDebounce<CallSearchField>(CallSearchField.CallerName, 1000)
    const [statuses, setStatuses, debouncedStatuses] = useStateWithDebounce<
        CallStatus[]
    >([], 750)
    const [directions, setDirections, debouncedDirections] =
        useStateWithDebounce<CallDirection[]>([], 750)
    const [automatedFilter, setAutomatedFilter, debouncedAutomatedFilter] =
        useStateWithDebounce<'all' | 'ai' | 'human'>('all', 500)

    const [orderBy, setOrderBy, debouncedOrderBy] =
        useStateWithDebounce<OrderByInput>(
            {
                column: 'created_at',
                isAscending: false,
            },
            250,
        )

    const [query, setQuery, debouncedQuery] = useStateWithDebounce<string>('')
    const { page, setPage, pageSize, setPageSize } = usePageControls(
        25,
        JSON.stringify([
            dateRange,
            searchField,
            statuses,
            directions,
            automatedFilter,
            query,
        ]),
    )

    const queryInput = useMemo(() => {
        const retQueryInput: QueryInput[] = []

        if (debouncedDates) {
            if (debouncedDates instanceof Date) {
                retQueryInput.push(
                    gteFloatFilter(
                        'created_at',
                        startOfDay(debouncedDates).getTime(),
                    ),
                )
                retQueryInput.push(
                    lteFloatFilter(
                        'created_at',
                        endOfDay(debouncedDates).getTime(),
                    ),
                )
            } else {
                if (isSameDay(debouncedDates[0], debouncedDates[1])) {
                    retQueryInput.push(
                        gteFloatFilter(
                            'created_at',
                            startOfDay(debouncedDates[0]).getTime(),
                        ),
                    )
                    retQueryInput.push(
                        lteFloatFilter(
                            'created_at',
                            endOfDay(debouncedDates[0]).getTime(),
                        ),
                    )
                } else {
                    retQueryInput.push(
                        gteFloatFilter(
                            'created_at',
                            startOfDay(debouncedDates[0]).getTime(),
                        ),
                    )
                    retQueryInput.push(
                        lteFloatFilter(
                            'created_at',
                            endOfDay(debouncedDates[1]).getTime(),
                        ),
                    )
                }
            }
        }

        if (debouncedStatuses.length > 0) {
            retQueryInput.push(inStringArrFilter('status', debouncedStatuses))
        }

        if (debouncedDirections.length > 0) {
            retQueryInput.push(
                inStringArrFilter('direction', debouncedDirections),
            )
        }

        if (debouncedAutomatedFilter === 'ai') {
            retQueryInput.push(eqBoolFilter('is_automated', true))
        } else if (debouncedAutomatedFilter === 'human') {
            retQueryInput.push(eqBoolFilter('is_automated', false))
        }

        if (debouncedQuery) {
            retQueryInput.push(
                iLikeStringFilter(
                    getCallSearchColumn(debouncedSearchField),
                    debouncedQuery,
                ),
            )
        }

        return retQueryInput
    }, [
        debouncedDates,
        debouncedStatuses,
        debouncedDirections,
        debouncedAutomatedFilter,
        debouncedQuery,
        debouncedSearchField,
    ])

    return {
        queryInput,
        debouncedOrderBy,
        orderBy,
        setOrderBy,

        page,
        setPage,
        pageSize,
        setPageSize,
        dateRange,
        setDateRange,
        query,
        setQuery,
        debouncedQuery,
        searchField,
        setSearchField,
        statuses,
        setStatuses,
        directions,
        setDirections,
        automatedFilter,
        setAutomatedFilter,
    }
}

export type QueryInputValues = {
    queryInput: QueryInput[]
    orderBy: OrderByInput
    setOrderBy: SetStateFn<OrderByInput>
    debouncedOrderBy: OrderByInput
}

export type QueryInputType = {
    page: number
    setPage: SetStateFn<number>
    pageSize: PageSizeType
    setPageSize: SetStateFn<PageSizeType>

    dateRange: Date | Date[] | undefined
    setDateRange: SetStateFn<Date | Date[] | undefined>
    query: string
    setQuery: SetStateFn<string>
    debouncedQuery: string
    searchField: CallSearchField
    setSearchField: SetStateFn<CallSearchField>
    statuses: CallStatus[]
    setStatuses: SetStateFn<CallStatus[]>
    directions: CallDirection[]
    setDirections: SetStateFn<CallDirection[]>
    automatedFilter: 'all' | 'ai' | 'human'
    setAutomatedFilter: SetStateFn<'all' | 'ai' | 'human'>
}
